import { useEffect, useMemo, useState, useRef } from "react";
import {
  Modal,
  Button,
  Select,
  Spinner,
  Alert,
  Label,
  TextInput,
  Textarea,
  Checkbox,
} from "flowbite-react";
import { useForm } from "react-hook-form";
import { LEADS, USERS, TAGS } from "../../config/apiConfig";
import axiosInstance from "../../config/axiosInstance";
import axios from "axios";

/* ============================================================
 * Helpers
 * ============================================================ */
const onlyDigits = (v = "") => String(v).replace(/\D/g, "");

const maskCpfCnpj = (value = "") => {
  const v = onlyDigits(value).slice(0, 14);
  if (v.length <= 11) {
    return v
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return v
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

const maskPhoneBR = (value = "") => {
  const v = onlyDigits(value).slice(0, 11);
  if (v.length <= 10) {
    return v.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  }
  return v.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
};

const isCpfOrCnpj = (doc = "") => {
  const d = onlyDigits(doc);
  if (!d) return null;
  return d.length > 11 ? "CNPJ" : "CPF";
};

const LEAD_SOURCE = [
  "manual",
  "website",
  "whatsapp",
  "instagram",
  "facebook",
  "google_ads",
  "organic",
  "referral",
  "event",
  "import",
];

/* ============================================================
 * Componente (mesmos campos do LeadCreateForm)
 * ============================================================ */
export default function QuickCreateCustomer({
  show,
  onClose,
  onCreated,
  onUpdated,
  mode = "create",
  initialCompany = null,
}) {
  const {
    handleSubmit,
    register,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "",
      companyName: "",
      website: "",
      document: "",
      source: "manual",
      tagId: "",
      notes: "",
      consentMarketing: false,
      nextAction: "",
      nextActionAt: "",
      nextActionNotes: "",
      ownerId: "",
    },
  });

  const [loadingCNPJ, setLoadingCNPJ] = useState(false);
  const [lockByCNPJ, setLockByCNPJ] = useState(false);
  const [errorCNPJ, setErrorCNPJ] = useState("");
  const [submitError, setSubmitError] = useState("");
  const errorBoxRef = useRef(null);

  const [ownerOptions, setOwnerOptions] = useState([]); // {value,label}
  const [tagOptions, setTagOptions] = useState([]); // {value,label}
  const [customFieldSchema, setCustomFieldSchema] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});

  const docType = useMemo(
    () => isCpfOrCnpj(watch("document")),
    [watch("document")]
  );

  // Carrega opções de responsáveis e tags
  useEffect(() => {
    (async () => {
      try {
        const [uRes, tRes] = await Promise.all([
          axiosInstance.get(`${USERS.GET_USERS}?itemsPerPage=-1`),
          axiosInstance
            .get(`${TAGS}?itemsPerPage=-1`)
            .catch(() => ({ data: { data: { data: [] } } })),
        ]);
        const owners = (uRes?.data?.data?.data || []).map((u) => ({
          value: u.id,
          label: u.name || u.email,
        }));
        const tags = (tRes?.data?.data?.data || []).map((t) => ({
          value: t.id,
          label: t.name,
        }));
        setOwnerOptions(owners);
        setTagOptions(tags);
      } catch (e) {
        console.error("QuickCreateCustomer: load options", e);
      }
    })();
  }, []);

  useEffect(() => {
    let canceled = false;
    const loadFields = async () => {
      try {
        const { data } = await axiosInstance.get(LEADS.CUSTOM_FIELDS);
        if (canceled) return;
        const items = Array.isArray(data?.data) ? data.data : [];
        setCustomFieldSchema(items.filter((field) => field.isRequired));
      } catch (error) {
        console.error("QuickCreateCustomer: load custom fields", error);
      }
    };
    loadFields();
    return () => {
      canceled = true;
    };
  }, []);

  // Edição: preencher quando abrir
  useEffect(() => {
    if (show && mode === "edit" && initialCompany) {
      reset({
        name: initialCompany.name || "",
        companyName: initialCompany.companyName || "",
        email: initialCompany.email || "",
        phoneNumber: initialCompany.phoneNumber || "",
        website: initialCompany.website || "",
        document: initialCompany.document || "",
        source: initialCompany.source || "manual",
        tagId: initialCompany.tagId || "",
        notes: initialCompany.notes || "",
        consentMarketing: !!initialCompany.consentMarketing,
        nextAction: initialCompany.nextAction || "",
        nextActionAt: initialCompany.nextActionAt || "",
        nextActionNotes: initialCompany.nextActionNotes || "",
        ownerId: initialCompany.ownerId || "",
      });
      if (Array.isArray(initialCompany.customFieldValues)) {
        const mapped = {};
        initialCompany.customFieldValues.forEach((item) => {
          if (item?.customFieldId) {
            mapped[item.customFieldId] = item.value;
          }
        });
        setCustomFieldValues(mapped);
      }
    }
  }, [show, mode, initialCompany, reset]);

  // Auto-fetch CNPJ (debounced + cancelável) — preenche companyName, email, phone, website
  useEffect(() => {
    let active = true;
    let controller = new AbortController();

    const run = async () => {
      setErrorCNPJ("");
      const digits = onlyDigits(watch("document"));
      if (digits.length !== 14) return;

      setLoadingCNPJ(true);
      setLockByCNPJ(true);
      try {
        const { data } = await axios.get(`https://api.opencnpj.org/${digits}`, {
          signal: controller.signal,
        });
        if (!active) return;
        const tel0 =
          (Array.isArray(data?.telefones) && data.telefones[0]) || {};
        setValue("name", data?.razao_social || "");
        setValue(
          "companyName",
          data?.nome_fantasia || data?.razao_social || ""
        );
        setValue("email", String(data?.email || "").toLowerCase());
        setValue(
          "phoneNumber",
          maskPhoneBR(`${tel0?.ddd || ""}${tel0?.numero || ""}`)
        );
        if (!watch("name")) setValue("name", data?.fantasia || "");
        if (data?.website) setValue("website", data.website);
      } catch (e) {
        if (!active) return;
        if (!(axios.isCancel?.(e) || e?.name === "CanceledError")) {
          console.error(e);
          setErrorCNPJ(
            e?.response?.data?.message ||
              "Não foi possível buscar o CNPJ agora."
          );
        }
      } finally {
        if (active) {
          setLoadingCNPJ(false);
          setLockByCNPJ(false);
        }
      }
    };

    const h = setTimeout(run, 450);
    return () => {
      active = false;
      controller.abort();
      clearTimeout(h);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("document")]);

  const handleCustomFieldChange = (fieldId, value) => {
    setCustomFieldValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const onSubmit = async (data) => {
    setSubmitError("");
    const missingField = customFieldSchema.find((field) => {
      const raw = customFieldValues[field.id];
      return !String(raw ?? "").trim();
    });
    if (missingField) {
      setSubmitError(`Preencha o campo "${missingField.label}".`);
      return;
    }
    const payload = {
      name: data.name?.trim(),
      email: data.email?.trim() || null,
      phoneNumber: data.phoneNumber || null,
      companyName: data.companyName?.trim() || null,
      website: data.website?.trim() || null,
      document: data.document || null,
      source: data.source || "manual",
      tagId: data.tagId || null,
      notes: data.notes || null,
      consentMarketing: !!data.consentMarketing,
      nextAction: data.nextAction || null,
      nextActionAt: data.nextActionAt || null,
      nextActionNotes: data.nextActionNotes || null,
      ownerId: data.ownerId || null,
      customFieldValues: customFieldSchema
        .map((field) => {
          const raw = customFieldValues[field.id];
          if (!String(raw ?? "").trim()) return null;
          return { customFieldId: field.id, value: raw };
        })
        .filter(Boolean),
    };

    try {
      if (mode === "edit" && initialCompany?.id) {
        await axiosInstance.put(
          `${LEADS.UPDATE}/${initialCompany.id}`,
          payload
        );
        onUpdated?.(initialCompany.id, payload);
        onClose?.();
      } else {
        const res = await axiosInstance.post(LEADS.CREATE, payload);
        const created = res?.data?.data || res?.data || null;
        if (created?.id) {
          onCreated?.(created);
          onClose?.();
          reset();
        }
      }
    } catch (err) {
      console.error("Salvar lead", err);
      let msg = "Não foi possível salvar. Tente novamente.";
      const status = err?.response?.status;
      const apiMsg = err?.response?.data?.message || err?.response?.data?.error;
      if (status === 409) msg = apiMsg || "Registro já existe (conflito).";
      else if (status === 400 || status === 422)
        msg = apiMsg || "Dados inválidos.";
      else if (apiMsg) msg = apiMsg;
      setSubmitError(msg);
      requestAnimationFrame(() => {
        errorBoxRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    }
  };

  const renderCustomFieldInput = (field) => {
    const value = customFieldValues[field.id] ?? "";
    const optionList = Array.isArray(field.options)
      ? field.options
      : typeof field.options === "string" && field.options.length
      ? field.options
          .split(",")
          .map((opt) => opt.trim())
          .filter(Boolean)
      : [];

    const onTextChange = (next) =>
      handleCustomFieldChange(field.id, next ?? "");

    switch (field.type) {
      case "date":
        return (
          <TextInput
            type="date"
            value={value}
            onChange={(event) => onTextChange(event.target.value)}
            required
          />
        );
      case "datetime":
        return (
          <TextInput
            type="datetime-local"
            value={value}
            onChange={(event) => onTextChange(event.target.value)}
            required
          />
        );
      case "number":
      case "currency":
      case "percent":
        return (
          <TextInput
            type="number"
            step="any"
            value={value}
            onChange={(event) => onTextChange(event.target.value)}
            required
          />
        );
      case "select":
        return (
          <Select
            value={value || ""}
            onChange={(event) => onTextChange(event.target.value)}
            required
          >
            <option value="">Selecione</option>
            {optionList.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        );
      case "radio":
        return (
          <div className="flex flex-wrap gap-3">
            {optionList.map((option) => (
              <label
                key={option}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <input
                  type="radio"
                  name={`custom-field-${field.id}`}
                  value={option}
                  checked={value === option}
                  onChange={() => handleCustomFieldChange(field.id, option)}
                />
                {option}
              </label>
            ))}
          </div>
        );
      case "checkbox":
      case "boolean":
        return (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={
                value === "true" ||
                value === true ||
                value === "1" ||
                value === 1
              }
              onChange={(event) =>
                handleCustomFieldChange(
                  field.id,
                  event.target.checked ? "true" : ""
                )
              }
            />
            <span className="text-sm text-gray-700">
              {field.placeholder || "Selecionado"}
            </span>
          </div>
        );
      case "textarea":
        return (
          <Textarea
            rows={3}
            value={value}
            onChange={(event) => onTextChange(event.target.value)}
            required
          />
        );
      default:
        return (
          <TextInput
            type="text"
            value={value}
            onChange={(event) => onTextChange(event.target.value)}
            placeholder={field.placeholder || ""}
            required
          />
        );
    }
  };

  const renderCustomFieldsSection = () => {
    if (!customFieldSchema.length) return null;
    return (
      <div className="space-y-3">
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
          Campos obrigatórios
        </p>
        <div className="grid grid-cols-1 gap-3">
          {customFieldSchema.map((field) => (
            <div key={field.id} className="space-y-1">
              <Label value={field.label || "Campo adicional"} />
              {renderCustomFieldInput(field)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Modal show={show} onClose={onClose} size="4xl">
      <Modal.Header>
        {mode === "edit" ? "Editar Contato" : "Novo Contato"}
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Documento */}

          {/* Grid campos principais */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 ">
            <div>
              <Label htmlFor="name" value="Nome do contato *" />
              <TextInput
                id="name"
                required
                disabled={lockByCNPJ}
                {...register("name", { required: true })}
              />
              {errors.name && (
                <small className="text-red-600">Informe o nome.</small>
              )}
            </div>

            <div>
              <Label htmlFor="phoneNumber" value="Telefone *" />
              <TextInput
                id="phoneNumber"
                placeholder="(11) 91234-5678"
                value={watch("phoneNumber") || ""}
                disabled={lockByCNPJ}
                onChange={(e) =>
                  setValue("phoneNumber", maskPhoneBR(e.target.value))
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="email" value="E-mail" />
              <TextInput
                id="email"
                type="email"
                placeholder="contato@empresa.com"
                disabled={lockByCNPJ}
                {...register("email")}
              />
            </div>
          </div>

          {renderCustomFieldsSection()}

          {submitError && (
            <div ref={errorBoxRef}>
              <Alert color="failure">{submitError}</Alert>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              color="gray"
              type="button"
              onClick={() => {
                onClose?.();
                if (mode === "create") reset();
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting || lockByCNPJ}>
              {isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
